interface DownscaleResult {
  /** data:image/jpeg;base64,... payload ready to POST to the Worker. */
  dataUrl: string;
  /** Width of the downscaled canvas. */
  width: number;
  /** Height of the downscaled canvas. */
  height: number;
  /** Approximate byte size of the base64 payload (useful for telemetry / guardrails). */
  approxBytes: number;
}

interface DownscaleOptions {
  maxEdge?: number;
  quality?: number;
  maxBytes?: number;
}

interface ScanImagePayload {
  imageBase64: string;
}

/**
 * Downscale the source image to `maxEdge` on its longest edge and re-encode as JPEG.
 * Accepts any `CanvasImageSource` (HTMLVideoElement, HTMLImageElement, ImageBitmap, OffscreenCanvas).
 *
 * The source is *not* mutated. If the source is an `HTMLVideoElement`, its current frame is captured.
 * Callers should `URL.revokeObjectURL` on any blob URLs they created before the source.
 */
function downscaleToJpeg(
  source: CanvasImageSource,
  { maxEdge = 1024, quality = 0.85, maxBytes }: DownscaleOptions = {}
): DownscaleResult {
  const { sourceWidth, sourceHeight } = readSourceSize(source);
  if (sourceWidth <= 0 || sourceHeight <= 0) {
    throw new Error('Image source has no readable dimensions');
  }

  const qualitySteps = maxBytes
    ? uniqueNumbers([quality, 0.86, 0.78, 0.7, 0.62, 0.54])
    : [quality];
  const edgeSteps = maxBytes
    ? uniqueNumbers([maxEdge, 1800, 1600, 1400, 1200, 1024, 900, 768]).filter(
        (edge) => edge <= maxEdge
      )
    : [maxEdge];

  let last: DownscaleResult | null = null;
  for (const edge of edgeSteps) {
    for (const q of qualitySteps) {
      const encoded = encodeToJpeg(source, sourceWidth, sourceHeight, edge, q);
      last = encoded;
      if (!maxBytes || encoded.approxBytes <= maxBytes) {
        return encoded;
      }
    }
  }

  if (last) return last;
  throw new Error('Could not encode image');
}

/**
 * Captures one high-quality camera frame for the Worker OCR pass.
 *
 * The upgraded OCR model handles full-frame ticket reads well, and one image
 * keeps request cost predictable. Older clients may still include extra image
 * view fields, but the Worker ignores them and scans the primary image.
 */
export function captureScanImage(
  video: HTMLVideoElement,
  { maxBytes = 950_000 }: { maxBytes?: number } = {}
): ScanImagePayload {
  const full = downscaleToJpeg(video, { maxEdge: 1600, quality: 0.9, maxBytes });

  return {
    imageBase64: full.dataUrl,
  };
}

function encodeToJpeg(
  source: CanvasImageSource,
  sourceWidth: number,
  sourceHeight: number,
  maxEdge: number,
  quality: number
): DownscaleResult {
  const scale = Math.min(1, maxEdge / Math.max(sourceWidth, sourceHeight));
  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Canvas 2d context unavailable');
  }
  ctx.drawImage(source, 0, 0, width, height);
  const dataUrl = canvas.toDataURL('image/jpeg', quality);

  // Clear the canvas to release the decoded bitmap ASAP.
  ctx.clearRect(0, 0, width, height);
  canvas.width = 0;
  canvas.height = 0;

  return {
    dataUrl,
    width,
    height,
    approxBytes: Math.ceil((dataUrl.length * 3) / 4),
  };
}

function uniqueNumbers(values: number[]): number[] {
  const out: number[] = [];
  for (const value of values) {
    if (Number.isFinite(value) && value > 0 && !out.includes(value)) {
      out.push(value);
    }
  }
  return out;
}

function readSourceSize(source: CanvasImageSource): {
  sourceWidth: number;
  sourceHeight: number;
} {
  if (source instanceof HTMLVideoElement) {
    return { sourceWidth: source.videoWidth, sourceHeight: source.videoHeight };
  }
  if (source instanceof HTMLImageElement) {
    return {
      sourceWidth: source.naturalWidth || source.width,
      sourceHeight: source.naturalHeight || source.height,
    };
  }
  if (source instanceof HTMLCanvasElement) {
    return { sourceWidth: source.width, sourceHeight: source.height };
  }
  // ImageBitmap / OffscreenCanvas
  const anySrc = source as unknown as { width: number; height: number };
  return { sourceWidth: anySrc.width ?? 0, sourceHeight: anySrc.height ?? 0 };
}
